import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {Box, Button, Stack} from '@mui/material';
import {translations} from '../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {useContext, useState} from 'react';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {AlertModal} from '../../../../../../components/alert-modal/alert-modal';
import {User} from '../../../../../../backend/interfaces/user';
import {UsersRepository} from '../../../../../../repositories/users';
import {UserType} from '../../../../../../backend/enums/user-type';
import {AppContext} from '../../../../../../contexts/app/context';
import {AppContextType} from '../../../../../../contexts/app/types';
import AddIcon from '@mui/icons-material/Add';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {usePaginatedTable} from '../../../../../../hooks';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Gender = 'gender',
  State = 'state',
}

export default function SeniorsSystemAdministratorOfficeScreen() {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Translations
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Hold state
  const [deletedSeniorName, setDeletedSeniorName] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.SeniorsDeletedQuery)
  );

  // Fetch seniors
  const handleGetSeniors = async (limit: number, skip: number, search?: string) => {
    // Load seniors
    const result: {totalCount: number; data: Array<User>} =
      await UsersRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        UserType.Senior,
        appContext.state.configuration.product,
        undefined,
        search
      );

    // Process data
    const processedData: PaginatedTableData<User> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.picture,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.Gender,
            value: obj.gender ? t(translations.genders[obj.gender]) : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.seniors.root.state_value_true)
              : t(translations.seniors.root.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedSeniorName(null);

    // Reset history
    history.replace(path);
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<User>({
    fetchData: handleGetSeniors,
  });

  return (
    <>
      <GenericHeader
        pageTitle={t(translations.seniors.root.title)}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.seniors.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={
          <Button
            endIcon={<AddIcon />}
            onClick={() => {
              if (path.charAt(url.length - 1) === '/') {
                history.push(`${path}create`);
              } else {
                history.push(`${path}/create`);
              }
            }}
            variant="inverted"
          >
            {t(translations.seniors.root.add_button)}
          </Button>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{type: 'user'}}
            emptyTableMessage={t(translations.seniors.root.no_seniors)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.seniors.root.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Gender,
                title: t(translations.seniors.root.gender_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.seniors.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows
          />
          <AlertModal
            id="senior-deleted-warning"
            opened={deletedSeniorName ? true : false}
            title={t(translations.seniors.root.deleted_senior_dialog_title, {
              name: deletedSeniorName,
            })}
            content={t(translations.seniors.root.deleted_senior_dialog_message)}
            handleClose={handleCloseDialog}
            actions={[
              {
                label: t(translations.seniors.root.deleted_senior_dialog_ok_button),
                type: 'neutral',
                handleClick: handleCloseDialog,
              },
            ]}
          />
        </Box>
      </Box>
    </>
  );
}
